import React from 'react';
import { Link } from 'react-router-dom';
import {
  Instagram, Facebook, Linkedin, MapPin, Phone, Mail,
  Clock, Calendar, MessageCircle, PawPrint,
} from 'lucide-react';
import { CONTACT } from '../data/mock';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const Footer = () => {
  return (
    <footer className="bg-[#1a0b3d] text-white pt-20 pb-8 relative overflow-hidden">
      <PawPrint className="absolute top-10 right-10 w-32 h-32 text-white/5" />
      <PawPrint className="absolute bottom-10 left-10 w-24 h-24 text-white/5 rotate-12" />

      <div className="max-w-[1280px] mx-auto px-5 lg:px-10 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-10 pb-14">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src={CONTACT.logo}
              alt="Ichi and Ori Pet Care"
              className="h-20 w-auto object-contain brightness-0 invert opacity-95 mb-5"
            />
            <p className="text-white/70 text-[14px] leading-relaxed mb-5 max-w-sm">
              Built by pet parents, trusted by pet parents. Veterinary care, grooming, boarding and pet essentials under one roof.
            </p>
            <div className="flex gap-3">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#e870b6] flex items-center justify-center transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#5b3eb5] flex items-center justify-center transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href={CONTACT.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#0a66c2] flex items-center justify-center transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[18px] font-bold mb-5">Services</h4>
            <ul className="space-y-3 text-white/75 text-[14px]">
              <li><Link to="/veterinary" className="hover:text-[#e870b6] transition-colors">Veterinary Services</Link></li>
              <li><Link to="/grooming" className="hover:text-[#e870b6] transition-colors">Grooming</Link></li>
              <li><Link to="/boarding" className="hover:text-[#e870b6] transition-colors">Day Boarding</Link></li>
              <li><Link to="/retail" className="hover:text-[#e870b6] transition-colors">Retail / Products</Link></li>
              <li><Link to="/home-veterinary-visit-noida" className="hover:text-[#e870b6] transition-colors">Home Veterinary Visits</Link></li>
            </ul>
          </div>

          {/* Popular Searches */}
          <div>
            <h4 className="text-[18px] font-bold mb-5">Popular Searches</h4>
            <ul className="space-y-3 text-white/75 text-[14px]">
              <li><Link to="/veterinary-clinic-noida" className="hover:text-[#e870b6] transition-colors">Veterinary Clinic Noida</Link></li>
              <li><Link to="/dog-vet-noida" className="hover:text-[#e870b6] transition-colors">Dog Vet Noida</Link></li>
              <li><Link to="/cat-vet-noida" className="hover:text-[#e870b6] transition-colors">Cat Vet Noida</Link></li>
              <li><Link to="/pet-vaccination-noida" className="hover:text-[#e870b6] transition-colors">Pet Vaccination Noida</Link></li>
              <li><Link to="/pet-surgery-noida" className="hover:text-[#e870b6] transition-colors">Pet Surgery Noida</Link></li>
              <li><Link to="/pet-diagnostics-noida" className="hover:text-[#e870b6] transition-colors">Pet Diagnostics Noida</Link></li>
              <li><Link to="/pet-x-ray-noida" className="hover:text-[#e870b6] transition-colors">Pet X-Ray Noida</Link></li>
              <li><Link to="/spaying-surgery-noida" className="hover:text-[#e870b6] transition-colors">Spaying Surgery Noida</Link></li>
              <li><Link to="/neutering-surgery-noida" className="hover:text-[#e870b6] transition-colors">Neutering Surgery Noida</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[18px] font-bold mb-5">Contact</h4>
            <div className="space-y-4 text-white/80 text-[14px]">
              <div className="flex gap-3 items-start">
                <MapPin size={16} className="mt-1 text-[#e870b6] flex-shrink-0" />
                <p className="leading-relaxed">{CONTACT.address}</p>
              </div>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('footer')} className="flex gap-3 items-center hover:text-[#e870b6] transition-colors">
                <Phone size={16} className="text-[#e870b6]" />
                <span>{CONTACT.phone}</span>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex gap-3 items-center hover:text-[#e870b6] transition-colors">
                <Mail size={16} className="text-[#e870b6]" />
                <span className="break-all">{CONTACT.email}</span>
              </a>
              <div className="flex gap-3 items-center">
                <Clock size={16} className="text-[#e870b6]" />
                <span>{CONTACT.hours}</span>
              </div>
            </div>
          </div>

          {/* Need Help */}
          <div>
            <h4 className="text-[18px] font-bold mb-5">Need Help?</h4>
            <ul className="space-y-3 text-white/80 text-[14px]">
              <li>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  onClick={() => trackCallClick('footer_need_help')}
                  className="inline-flex items-center gap-2 hover:text-[#e870b6] transition-colors"
                >
                  <Phone size={14} className="text-[#e870b6] flex-shrink-0" />
                  <span>Emergency Consultation? <span className="text-[#e870b6] font-semibold">Call us immediately.</span></span>
                </a>
              </li>
              <li>
                <Link
                  to="/home-veterinary-visit-noida"
                  className="inline-flex items-center gap-2 hover:text-[#e870b6] transition-colors"
                >
                  <MapPin size={14} className="text-[#e870b6] flex-shrink-0" />
                  Home Veterinary Visits
                </Link>
              </li>
              <li>
                <Link
                  to="/book"
                  onClick={() => trackBookButtonClick('footer_need_help')}
                  className="inline-flex items-center gap-2 hover:text-[#e870b6] transition-colors"
                >
                  <Calendar size={14} className="text-[#e870b6] flex-shrink-0" />
                  Book an Appointment
                </Link>
              </li>
              <li>
                <a
                  href={`https://wa.me/${CONTACT.phoneRaw.replace('+', '')}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackWhatsAppClick('footer_need_help')}
                  className="inline-flex items-center gap-2 hover:text-[#e870b6] transition-colors"
                >
                  <MessageCircle size={14} className="text-[#25d366] flex-shrink-0" />
                  WhatsApp Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-white/55 text-[13px]">
          <p>&copy; {new Date().getFullYear()} Ichi and Ori Pet Care. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/gallery" className="hover:text-white transition-colors">Gallery</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
